"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Scan,
  Map,
  Sparkles,
  LayoutGrid,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import Link from "next/link";

const features = [
  {
    id: "feature-0",
    label: "IMPORTAZIONE AUTOMATICA",
    icon: Download,
    title: "Migrare non è più un incubo",
    problem:
      "Passare da Excel o da un gestionale vecchio richiede settimane di lavoro, errori e clienti persi.",
    solution:
      "Importiamo automaticamente Clienti, Mandanti, Ordini e Fatture. Mapping intelligente, deduplicazione automatica, zero downtime.",
    result: "Sei operativo in 24-48 ore, non in 3 mesi.",
    metric: "Setup medio: 36 ore",
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    gradient: "from-blue-50/50 to-transparent",
  },
  {
    id: "feature-1",
    label: "AI OCR — DOCUMENTI A DATI",
    icon: Scan,
    title: "Una foto vale più di mille digitazioni",
    problem:
      "Passare ordini cartacei e fatture PDF richiede ore di noioso data entry ogni giorno.",
    solution:
      "Scatta una foto o carica un PDF. L'AI estrae automaticamente i dati e li struttura. Il sistema impara dai tuoi documenti migliorando nel tempo.",
    result: "Risparmi 5-10 ore a settimana di data entry manuale.",
    metric: "OCR accuracy: 98.5%",
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    gradient: "from-purple-50/50 to-transparent",
  },
  {
    id: "feature-2",
    label: "MAPPA DEL TERRITORIO",
    icon: Map,
    title: "Vedi ciò che gli altri non vedono",
    problem:
      "Non sai a chi manchi da troppo tempo. Pianificare il giro visite è un'arte oscura.",
    solution:
      "Mappa interattiva con codifica visiva: verde = ha ordinato, giallo = non ancora, rosso = ha saltato la stagione. Pianificazione ottimizzata del percorso.",
    result: "Nessun cliente dimenticato. Visite più efficienti. Più ordini.",
    metric: "Clienti ripescati: +23%",
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    gradient: "from-emerald-50/50 to-transparent",
  },
  {
    id: "feature-3",
    label: "AI PROSPECTING",
    icon: Sparkles,
    title: "La pipeline si riempie da sola",
    problem:
      "Trovare nuovi clienti è costoso. Il cold calling non funziona più.",
    solution:
      "L'AI analizza il tuo storico e trova potenziali clienti simili ai tuoi migliori. Scoring di potenziale e ice-breaker automatici per ogni approccio.",
    result: "Pipeline sempre piena di opportunità qualificate.",
    metric: "Lead conversion: +40%",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    gradient: "from-amber-50/50 to-transparent",
  },
  {
    id: "feature-4",
    label: "PORTFOLIO BRAND",
    icon: LayoutGrid,
    title: "Ogni opportunità al suo posto",
    problem:
      "Decine di mandanti, centinaia di varianti, prezzi che cambiano. È facile perdere il controllo.",
    solution:
      "Catalogo prodotti strutturato con categorie merceologiche, competitor tracking e analisi gap. Vedi dove puoi espandere l'offerta.",
    result: "Portfolio ottimizzato. Nessuna opportunità persa.",
    metric: "Opportunità identificate: +35%",
    iconBg: "bg-rose-50",
    iconColor: "text-rose-600",
    gradient: "from-rose-50/50 to-transparent",
  },
  {
    id: "feature-5",
    label: "PERFORMANCE & ANALYTICS",
    icon: TrendingUp,
    title: "Decidi con i dati, non con l'intuito",
    problem:
      "Non sai quali clienti valgono davvero. Decidi a sensazione, non su dati concreti.",
    solution:
      "CLV, analisi stagionali SS/FW, performance per mandante e zona, sales goals con tracking automatico. Tutto ciò che serve per decidere.",
    result: "Decisioni basate sui dati. Margine protetto.",
    metric: "Revenue ottimizzato: +18%",
    iconBg: "bg-cyan-50",
    iconColor: "text-cyan-600",
    gradient: "from-cyan-50/50 to-transparent",
  },
];

export function FeatureShowcase() {
  return (
    <section className="py-0">
      {features.map((feature, index) => {
        const isReversed = index % 2 === 1;
        const FeatureIcon = feature.icon;

        return (
          <div
            key={feature.id}
            id={feature.id}
            className={`py-20 md:py-32 px-4 sm:px-6 lg:px-8 ${
              index % 2 === 0 ? "bg-white" : "bg-honey-50/50"
            }`}
          >
            <div className="container mx-auto max-w-6xl">
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  isReversed ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Text Content */}
                <div className={`${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                  {/* Label */}
                  <span className="inline-block text-xs font-bold uppercase tracking-widest text-ruby-600 mb-4">
                    {feature.label}
                  </span>

                  {/* Title */}
                  <h2 className="font-serif text-3xl md:text-4xl font-bold text-mocha-950 tracking-tight mb-6">
                    {feature.title}
                  </h2>

                  {/* Problem */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-mocha-400 uppercase tracking-wide mb-2">
                      Il problema
                    </h3>
                    <p className="text-mocha-600 leading-relaxed">
                      {feature.problem}
                    </p>
                  </div>

                  {/* Solution */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-ruby-600 uppercase tracking-wide mb-2">
                      La soluzione KennyDB
                    </h3>
                    <p className="text-mocha-800 leading-relaxed">
                      {feature.solution}
                    </p>
                  </div>

                  {/* Result with checkmark */}
                  <div className="flex items-start gap-3 mb-6 p-4 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                        Il risultato
                      </h3>
                      <p className="text-emerald-800 font-medium">
                        {feature.result}
                      </p>
                    </div>
                  </div>

                  {/* Metric badge */}
                  <Badge
                    variant="secondary"
                    className="bg-mocha-100 text-mocha-700 mb-6"
                  >
                    {feature.metric}
                  </Badge>

                  {/* CTA */}
                  <Link href="/cta?type=trial&source=feature">
                    <Button className="bg-ruby-600 hover:bg-ruby-700 text-white">
                      Prova questa feature
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                {/* Visual/Illustration */}
                <div
                  className={`${
                    isReversed ? "lg:order-1" : "lg:order-2"
                  } relative`}
                >
                  <div
                    className={`aspect-[4/3] rounded-2xl bg-gradient-to-br ${feature.gradient} border border-mocha-100 flex items-center justify-center relative overflow-hidden`}
                  >
                    {/* Decorative background */}
                    <div className="absolute inset-0 opacity-50">
                      <div
                        className={`absolute -top-20 -right-20 w-64 h-64 rounded-full ${feature.iconBg} blur-3xl`}
                      />
                      <div
                        className={`absolute -bottom-20 -left-20 w-48 h-48 rounded-full ${feature.iconBg} blur-3xl`}
                      />
                    </div>

                    {/* Central icon */}
                    <div
                      className={`relative w-32 h-32 rounded-2xl ${feature.iconBg} flex items-center justify-center shadow-lg`}
                    >
                      <FeatureIcon className={`w-16 h-16 ${feature.iconColor}`} />
                    </div>

                    {/* Floating elements */}
                    <div className="absolute top-6 left-6 bg-white rounded-lg p-3 shadow-md">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-xs font-medium text-mocha-700">
                          Attivo
                        </span>
                      </div>
                    </div>

                    <div className="absolute bottom-6 right-6 bg-white rounded-lg p-3 shadow-md">
                      <div className="text-xs font-medium text-mocha-700">
                        Efficienza
                      </div>
                      <div className="text-lg font-bold text-emerald-600">
                        +85%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
